// Screens loaded when they are first opened, so the app's first file holds the
// frame (sidebar, header, realtime, query client) and not every screen.
//
// Promise:
// - A route's lazy component is loaded by the router together with the
//   route's data, before the route is drawn: the previous screen stays until
//   both are there, and no fallback is ever drawn in its place. Hovering a
//   link loads it too (the router's preload on intent).
// - A route component that draws lazy screens (`withLazy`) waits for them the
//   same way.
// - Outside a route a lazy component is drawn under a Suspense: the side
//   view's frame and the phone's slots have one (showing the detail's
//   skeleton, or nothing); anywhere else `lazyView` gives it one.
// - The opened screen's code starts loading before the app is drawn
//   (`loadScreensFor`), alongside what the host waits for at start.
// - Once the first screen is drawn, the other screens are loaded while the
//   browser is idle (`warmLazyRoutes`), one at a time, so moving later does
//   not wait for the network.
// - A file of an older build that is gone (after a deploy) reloads the page
//   (the host's handler of Vite's preload error); meanwhile the screen waits
//   instead of showing the error.
import {
  type AnyRoute,
  type AsyncRouteComponent,
  lazyRouteComponent,
} from "@tanstack/react-router";
import { type ComponentType, type ReactNode, Suspense } from "react";

/** Something that can be loaded ahead of drawing (a lazy component, or one that draws some). */
export interface Preloadable {
  preload?: () => Promise<void> | void;
}

/**
 * A component of a module loaded the first time it is needed:
 * `lazyScreen(() => import("./detail"), "CaseRoute")`. Use it as a route's
 * component, inside a route component given to `withLazy`, or under a
 * Suspense elsewhere (`lazyView`).
 */
export function lazyScreen<
  // biome-ignore lint/suspicious/noExplicitAny: a module's exports, as the router types them
  T extends Record<string, any>,
  K extends keyof T,
>(load: () => Promise<T>, name: K) {
  // A failed import the host handles (it reloads the page) resolves to
  // nothing: wait for the reload rather than draw the error.
  return lazyRouteComponent(
    () => load().then((mod) => mod ?? new Promise<T>(() => {})),
    name,
  );
}

const loadAll = (lazies: readonly Preloadable[]) =>
  Promise.all(lazies.map((l) => l.preload?.())).then(() => {});

/**
 * A route component that draws lazy screens: the router loads them before
 * drawing it (as if the component itself were lazy).
 */
export function withLazy<C extends ComponentType<never>>(
  component: C,
  ...lazies: readonly Preloadable[]
): C & { preload(): Promise<void> } {
  return Object.assign(component, { preload: () => loadAll(lazies) });
}

/**
 * A lazy component drawn outside a route (a side view, a settings item, a
 * header's action): under its own Suspense, showing `fallback` until loaded.
 * `preload` loads it ahead (a route's loader that knows it will draw it).
 */
export function lazyView<P extends object>(
  Lazy: AsyncRouteComponent<P>,
  fallback: ReactNode = null,
): ComponentType<P> & { preload(): Promise<void> } {
  function LazyView(props: P) {
    return (
      <Suspense fallback={fallback}>
        <Lazy {...props} />
      </Suspense>
    );
  }
  return Object.assign(LazyView, { preload: () => loadAll([Lazy]) });
}

interface ChunkRouter {
  routesById: Record<string, AnyRoute>;
  loadRouteChunk(route: AnyRoute): unknown;
  getMatchedRoutes(
    pathname: string,
  ): readonly [readonly AnyRoute[], ...unknown[]];
}

const loadChunk = (router: ChunkRouter, route: AnyRoute) =>
  Promise.resolve(router.loadRouteChunk(route)).catch(() => undefined);

/**
 * Starts loading the screens of the routes `pathname` opens, before the app
 * is drawn (while the host still checks the session or restores its cache):
 * the first screen's code then does not wait for those. A failure is left to
 * the route (it tries again when it is drawn).
 */
export function loadScreensFor(router: ChunkRouter, pathname: string): void {
  for (const route of router.getMatchedRoutes(pathname)[0])
    void loadChunk(router, route);
}

/** When the browser has nothing else to do (soon after, where it cannot say). */
const whenIdle = (run: () => void) =>
  typeof requestIdleCallback === "function"
    ? requestIdleCallback(run, { timeout: 4000 })
    : setTimeout(run, 200);

/**
 * Loads the lazy screens of the routes `wanted` picks (by full path), one at
 * a time while the browser is idle. A failure is left to the route that needs
 * it later (it tries again then).
 */
export function warmLazyRoutes(
  router: ChunkRouter,
  wanted: (fullPath: string) => boolean,
): void {
  const routes = Object.values(router.routesById).filter(
    (r) =>
      wanted(String(r.fullPath)) &&
      typeof (r.options.component as Preloadable | undefined)?.preload ===
        "function",
  );
  const next = () => {
    const route = routes.shift();
    if (!route) return;
    whenIdle(() => void loadChunk(router, route).then(next));
  };
  next();
}
